/**
 * Sidebar items and `/main` routes gated behind the Standard or Premium
 * subscription plan. Anything NOT listed here (dashboard, menu, restaurant
 * profile, billing-and-subscription, settings pages, ...) is available on
 * every plan, including Free.
 *
 * This is a deny-list, not an allow-list: only known gated routes/titles are
 * restricted, so routes outside this menu spec (e.g. `settings`,
 * `promotions`, `serving-time`) are left untouched rather than silently
 * locked out.
 *
 * The sidebar shows a gated title rather than removing it (locked + an
 * "Upgrade to unlock" prompt on click, see sidebar.component.ts) — a visible,
 * locked item sells the upgrade; a hidden one just looks like the feature
 * doesn't exist. Direct/bookmarked navigation to a gated route is still
 * blocked by SubscriptionPlanGuard via `isRouteAllowedForPlan` below.
 *
 * `Tax` / `tax` is deliberately absent from every list here — it stays
 * governed solely by the `TAX` feature flag (see
 * sidebar.component.ts `configureSideMenus()`), independent of plan tier.
 */

export const STANDARD_AND_ABOVE_MENU_TITLES: string[] = [
  'Orders',
  'Tables',
  'QR codes',
  'Zones',
  'Staff',
  'Devices',
  'Stations',
  'To Go',
  'Payments',
  'Reports',
];

export const PREMIUM_ONLY_MENU_TITLES: string[] = ['Reservations', 'Delivery Partners'];

const STANDARD_AND_ABOVE_ROUTES: string[] = [
  'order',
  'add-order',
  'order-payment',
  'order-feedback',
  'table',
  'qr-codes',
  'zone',
  'staffs',
  'device',
  'station',
  'togo',
  'payment',
  'report',
];

const PREMIUM_ONLY_ROUTES: string[] = ['reservation', 'reservation-settings', 'delivery-partners'];

/** How gated sidebar titles read on a Manage Plans card: related titles share
 * one line, and each line says what the page does rather than just its menu
 * name. A title not listed here still shows, under its own menu name, so a
 * title added to the tier lists above always reaches the cards. A line with an
 * empty label is left off the cards: Orders & Payments are already sold by the
 * plan's own "Online ordering & pickup" feature. */
const PLAN_FEATURE_LINES: { titles: string[]; label: string }[] = [
  { titles: ['Orders', 'Payments'], label: '' },
  { titles: ['Tables', 'QR codes', 'Zones'], label: 'Tables & floor zones' },
  { titles: ['Staff'], label: 'Staff accounts & permissions' },
  { titles: ['Devices', 'Stations'], label: 'Devices, printers & kitchen stations' },
  { titles: ['To Go'], label: 'To Go & dining settings' },
  { titles: ['Reports'], label: 'Sales & order reports' },
  { titles: ['Reservations'], label: 'Table reservations' },
  { titles: ['Delivery Partners'], label: 'Delivery partner integrations' },
];

/** Sidebar titles a plan unlocks on top of the plan below it, from the same
 * lists that lock the sidebar and routes. Empty for Free and unknown names. */
export function menuTitlesAddedByPlan(planName: string | null | undefined): string[] {
  const name = (planName || '').toLowerCase();
  if (name === 'standard') return [...STANDARD_AND_ABOVE_MENU_TITLES];
  if (name === 'premium') return [...PREMIUM_ONLY_MENU_TITLES];
  return [];
}

/** Card lines for a set of gated titles, in the order the titles were listed.
 * A line's label is used only when all of its titles are in the set; a partial
 * group falls back to the menu names it does have. */
export function planFeatureLabels(titles: string[]): string[] {
  const labels: string[] = [];
  const used = new Set<string>();
  for (const title of titles) {
    if (used.has(title)) continue;
    const line = PLAN_FEATURE_LINES.find((l) => l.titles.includes(title));
    const members = line ? line.titles.filter((t) => titles.includes(t)) : [title];
    members.forEach((t) => used.add(t));
    const whole = line && members.length === line.titles.length;
    if (whole && !line.label) continue;
    labels.push(whole ? line.label : members.join(' & '));
  }
  return labels;
}

/** Default landing route when a plan-gated route is blocked. */
export const PLAN_MENU_DEFAULT_ROUTE = 'dashboard';

function firstSegment(path: string): string {
  return (path || '').split('?')[0].split('/').filter(Boolean)[0] || '';
}

/** The lowest plan tier that includes a given sidebar title, or null if every
 * plan (including Free) already includes it. Lets a lock message name the
 * exact plan a title needs, rather than a generic "a higher plan". */
export function minimumPlanForTitle(title: string): 'Standard' | 'Premium' | null {
  if (PREMIUM_ONLY_MENU_TITLES.includes(title)) {
    return 'Premium';
  }
  if (STANDARD_AND_ABOVE_MENU_TITLES.includes(title)) {
    return 'Standard';
  }
  return null;
}

/** Sidebar titles to show locked (not removed) for a given (resolved) plan name. */
export function menuTitlesLockedForPlan(planName: string | null | undefined): string[] {
  if (planName === 'Premium') return [];
  if (planName === 'Standard') return [...PREMIUM_ONLY_MENU_TITLES];
  if (planName === 'Free') return [...STANDARD_AND_ABOVE_MENU_TITLES, ...PREMIUM_ONLY_MENU_TITLES];
  return []; // unresolved/unknown plan name -> don't hide anything
}

/** Whether a `/main` child route (its configured path, e.g. `update-menu/:id`) is reachable on the given (resolved) plan name. */
export function isRouteAllowedForPlan(path: string, planName: string | null | undefined): boolean {
  if (planName !== 'Free' && planName !== 'Standard') {
    return true; // Premium, or an unresolved/unknown plan name -> don't block
  }
  const segment = firstSegment(path);
  if (PREMIUM_ONLY_ROUTES.includes(segment)) {
    return false;
  }
  if (planName === 'Free' && STANDARD_AND_ABOVE_ROUTES.includes(segment)) {
    return false;
  }
  return true;
}

/** Header "Add Quick Item" cards beyond Menu/Type & Category/Customization,
 * which every plan gets. Keyed by the header's quickAddItems `guard` (falling
 * back to `key`), not sidebar titles, since the two panels name the same
 * features differently. Mirrors the menu tiers above. */
export const STANDARD_AND_ABOVE_QUICK_ADD_GUARDS: string[] = ['table', 'zone', 'staffs', 'station'];
export const PREMIUM_ONLY_QUICK_ADD_GUARDS: string[] = [];

/** Whether a Quick Add card (identified by its `guard`/key) is available on the given (resolved) plan name. */
export function isQuickAddGuardAllowedForPlan(
  guard: string,
  planName: string | null | undefined,
): boolean {
  if (planName === 'Premium' || !planName) {
    return true; // Premium, or an unresolved/unknown plan name -> don't hide
  }
  if (PREMIUM_ONLY_QUICK_ADD_GUARDS.includes(guard)) {
    return false;
  }
  return planName !== 'Free' || !STANDARD_AND_ABOVE_QUICK_ADD_GUARDS.includes(guard);
}
